/*
TRANE CONFIDENTIAL

2021 – 2022 Copyright Trane U.S. Inc.
All Rights Reserved.

NOTICE: All information contained herein is, and remains the property of
Trane U.S. Inc. The source code, object code, intellectual and technical
concepts contained herein are proprietary to Trane U.S. Inc. and may be
covered by patents and patent applications and are protected by trade secret
and copyright law. Dissemination of this information or reproduction of this
material is strictly forbidden unless prior written permission is obtained
from Trane U.S. Inc. Do not use this software without the prior written
permission of Trane U.S. Inc.
*/

/* @flow */
import * as React from "react";
import Raven from "raven-js";
import Form from "nx-components/Form";
import { FORM_ERROR_ANIMATION_DELAY } from "nx-components/FormError";
import parseIconsFromServer from "nexia_mobile/parseIconsFromServer";
import UserAgent from "nx-components/UserAgent";
import type { FormField } from "nexia_mobile/api/types";
import cloneDeep from "lodash/cloneDeep";

type Props = {
  fields: Array<FormField>,
  fieldErrors: { [fieldName: string]: Array<string> },
  fieldValues: { [fieldName: string]: any },
  onChange?: (string, any) => void,
  onSubmit?: (Event) => void,
  onBlur?: (Event) => void,
  onEnterPressed?: () => void,
  autoFocusField?: string,
};

const isAndroid = UserAgent.isAndroid();

export default class SchemaFieldsForm extends React.Component<Props> {
  _wrapper: ?HTMLDivElement;
  /* public ref interface */
  scrollToField(fieldName: string) {
    setTimeout(
      () => this._scrollToField(fieldName),
      FORM_ERROR_ANIMATION_DELAY
    );
  }
  /* public ref interface end */

  _scrollToField(fieldName: string) {
    if (!this._wrapper) {
      return;
    }
    const fieldElement = this._wrapper.querySelector(
      `[data-field-name="${fieldName}"]`
    );
    if (fieldElement != null) {
      // $FlowIgnore scrollIntoViewIfNeeded is supported on all of our browsers
      if (typeof fieldElement.scrollIntoViewIfNeeded == "function") {
        fieldElement.scrollIntoViewIfNeeded();
      } else {
        fieldElement.scrollIntoView();
      }
    }
  }

  _massageFieldShape = (
    field: FormField,
    errorsForField: Array<string>,
    changedValue: any
  ) => {
    // $FlowFixMe cloning unions of object types via spread doesn't really work
    let newField: FormField = { ...field };

    // Create icons from icon strings
    if (newField.type === "multi") {
      this._massageMultiField(newField);
    } else if (newField.type === "range") {
      this._massageSliderField(newField);
    }

    if (errorsForField && errorsForField.length > 0) {
      newField.error = errorsForField[0];
    }

    if (changedValue != null) {
      if (newField.type === "color") {
        newField.warm = changedValue.warm;
        newField.cool = changedValue.cool;
        newField.red = changedValue.red;
        newField.green = changedValue.green;
        newField.blue = changedValue.blue;
      } else {
        newField.value = changedValue;
      }
    }

    return newField;
  };

  _massageMultiField = (newField: Object) => {
    if (newField.options && Array.isArray(newField.options)) {
      let dedupedOptions = [];
      let hasDupes = false;
      let prevValue = null;
      newField.options.forEach((option) => {
        if (prevValue != null && prevValue === option.value) {
          hasDupes = true;
          return; // don't display this duplicate option
        }
        prevValue = option.value;
        const icon = parseIconsFromServer(option.icon)[0];
        dedupedOptions.push({
          ...option,
          id: option.value,
          icon: icon && icon.element,
        });
      });
      if (hasDupes) {
        Raven.captureMessage(
          "MultiSelectField created with duplicate options",
          {
            level: "warning",
            extra: {
              options: newField.options,
            },
          }
        );
      }
      newField.options = dedupedOptions;
    }
    return newField;
  };

  _massageSliderField = (newField: Object) => {
    newField.stepValues = newField.step_values;
    newField.hasOffState = newField.has_off_state;
    if (
      newField.minButton != null &&
      newField.minButton.icon != null &&
      !React.isValidElement(newField.minButton.icon)
    ) {
      newField.minButton.icon = parseIconsFromServer(
        newField.minButton.icon
      )[0].element;
    }
    if (
      newField.maxButton != null &&
      newField.maxButton.icon != null &&
      !React.isValidElement(newField.maxButton.icon)
    ) {
      newField.maxButton.icon = parseIconsFromServer(
        newField.maxButton.icon
      )[0].element;
    }
    return newField;
  };

  render() {
    const {
      fields,
      fieldErrors,
      fieldValues,
      onChange,
      onSubmit,
      onBlur,
      onEnterPressed,
      autoFocusField,
      title,
      controllableThermostatsLabel,
      schema,
    } = this.props;

    let copy = cloneDeep(fields);
    let massagedFields;
    if (title) {
      let newFieldObj = {
        label: controllableThermostatsLabel,
        type: "label",
      };
      if (schema.get("phone_carrier_id")) {
        copy.splice(3, 0, newFieldObj);
      } else {
        copy.splice(4, 0, newFieldObj);
      }
      massagedFields = copy.map((field) =>
        this._massageFieldShape(
          field,
          fieldErrors[field.name],
          fieldValues[field.name]
        )
      );
    } else {
      massagedFields = fields.map((field) =>
        this._massageFieldShape(
          field,
          fieldErrors[field.name],
          fieldValues[field.name]
        )
      );
    }

    return (
      <div
        data-component-name="SchemaFieldsForm"
        ref={(el) => (this._wrapper = el)}
      >
        <Form
          fields={massagedFields}
          onChange={onChange}
          onSubmit={onSubmit}
          onBlur={onBlur}
          onEnterPressed={onEnterPressed}
          autoFocusField={autoFocusField}
          shouldScrollIntoView={isAndroid}
        />
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/SchemaFieldsForm.jsx